import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CASE_STUDIES, FILTERS, PORTFOLIO } from "../data/content";
import { MaskedLines, Reveal } from "../components/Motion";
import { CtaBand } from "../components/Cta";

export default function Work() {
  const [filter, setFilter] = useState("All");

  const filteredStudies = useMemo(
    () => (filter === "All" ? CASE_STUDIES : CASE_STUDIES.filter((cs) => cs.tags.includes(filter))),
    [filter]
  );
  const filteredPortfolio = useMemo(
    () => (filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.tags.includes(filter))),
    [filter]
  );

  return (
    <>
      <section className="page-hero simple" data-testid="work-hero">
        <div className="ph-content">
          <p className="crumb">
            <Link to="/">Home</Link> <span>/</span> Work
          </p>
          <h1>
            <MaskedLines lines={[<>Work that</>, <em key="w">works.</em>]} delay={0.2} />
          </h1>
          <p className="ph-intro">
            Full brand builds, single crafts, and everything in between. Four deep case studies below — and a wider reel
            of individual pieces for brands we&apos;ve moved.
          </p>
        </div>
      </section>

      <section className="work-filters section-pad" data-testid="work-filters-section">
        <div className="filter-chips" data-testid="work-filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`chip ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase()}-chip`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {filteredStudies.length > 0 && (
        <section className="case-studies section-pad" data-testid="case-studies-section">
          <Reveal className="section-heading">
            <p className="eyebrow">Case studies</p>
            <span className="section-count">Full brand builds &amp; deep dives</span>
          </Reveal>
          <div className="cs-grid">
            {filteredStudies.map((cs, i) => (
              <motion.div key={cs.slug} layout initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }}>
                <Link to={`/work/${cs.slug}`} className="project-card cs-card" data-testid={`case-study-${cs.slug}-card`}>
                  <img src={cs.heroImage} alt={cs.title} loading="lazy" />
                  <div className="project-shade" />
                  <div className="project-meta">
                    <span>{cs.scope}</span>
                    <span>{cs.year}</span>
                  </div>
                  <h3>{cs.title}</h3>
                  <span className="project-arrow">
                    <ArrowUpRight />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {filteredPortfolio.length > 0 && (
        <section className="portfolio-index section-pad" data-testid="portfolio-section">
          <Reveal className="section-heading">
            <p className="eyebrow">Selected pieces</p>
            <span className="section-count">Individual crafts, delivered solo</span>
          </Reveal>
          <div className="project-grid">
            {filteredPortfolio.map((piece, i) => (
              <motion.div
                key={piece.title}
                layout
                className={`reveal-card ${piece.size}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <div className={`project-card ${piece.size} static`} data-testid={`portfolio-${i + 1}-card`}>
                  <img src={piece.image} alt={piece.title} loading="lazy" />
                  <div className="project-shade" />
                  <div className="project-meta">
                    <span>{piece.category}</span>
                    <span>{piece.year}</span>
                  </div>
                  <h3>{piece.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {filteredStudies.length === 0 && filteredPortfolio.length === 0 && (
        <section className="section-pad" data-testid="work-empty-state">
          <p className="ph-intro">Nothing filed under this craft yet — check back soon, or ask us directly.</p>
        </section>
      )}

      <CtaBand testid="work-cta-band" />
    </>
  );
}
